import { type ClientItemId } from 'owa-client-ids';
import {
    getListViewState,
    getSelectedTableView,
    isItemPartOperation,
    MailRowDataPropertyGetter,
    getStore as getListViewStore,
    isMailListConversationView,
} from 'owa-mail-list-store';
import { type ClientItem } from 'owa-mail-store';
import { getItemToShowFromNodeId } from 'owa-mail-store/lib/utils/conversationsUtils';
import { getStore as getMailStore } from 'owa-mail-store/lib/store/Store';
import { isMeetingMessage } from 'owa-meeting-message-utils';
import type ItemId from 'owa-service/lib/contract/ItemId';

/**
 * Checks whether the current selection is a meeting message or a multiple‑item selection
 * for reminder display purposes.
 *
 * In parity with Win32, email reminders are disabled for meeting messages and for
 * multiple selections.
 *
 * - `rowKeys` refers to the selected items in the mail list.
 * - `selectedNodeIds` refers to the selected items in the expanded conversation view.
 *
 * If either contains more than one item, it is considered a multiple selection.
 *
 * All parameters are optional. If none are provided, the current selection in the mail
 * list is used, but at least one way to identify the item is required.
 *
 * If there is no selection and no parameters are provided, the function returns `true`
 * (undetermined), because there is no way to identify the item(s), disabling reminders by default.
 *
 * @param {ClientItemId | ItemId} [itemId] - Optional itemId to check directly.
 * @param {string} [rowKey] - Optional rowKey to check directly.
 * @param {string} [tableViewId] - Optional tableViewId to check directly.
 * @returns {boolean} - `true` if the current selection is invalid for reminders,
 * `false` if reminders should be display.
 */

export function isCurrentSelectionDisabledForReminders(
    itemId?: ClientItemId | ItemId,
    rowKey?: string,
    tableViewId?: string
): boolean {
    const tableView = getSelectedTableView();
    const rowKeys = tableView.isInVirtualSelectAllMode
        ? tableView.rowKeys
        : [...tableView.selectedRowKeys.keys()];
    const selectedNodeIds = getListViewState().expandedConversationViewState.selectedNodeIds;

    if (
        rowKeys.length <= 0 &&
        selectedNodeIds.length <= 0 &&
        !itemId &&
        (!rowKey || !tableViewId)
    ) {
        return true; // Undetermined selection, disable reminders by default.
    }

    if (rowKeys.length > 1 || selectedNodeIds.length > 1) {
        return true;
    }

    // This is the case when an specific item is being acted upon.
    if (itemId) {
        const item: ClientItem | undefined = getMailStore().items.get(itemId.Id);
        return item ? isMeetingMessage(item) : true; // If item is not found, disable reminders by default.
    }

    // Particular case when we have rowKey and tableViewId provided (FlagContextMenu in mail list without selection)
    if (rowKey && tableViewId) {
        const currentTableView = getListViewStore().tableViews.get(tableViewId);
        if (!currentTableView) {
            return true; // If tableView is not found, disable reminders by default.
        }
        const item = MailRowDataPropertyGetter.getItem(rowKey, currentTableView);
        //Note: getRowDataProperty needs to be used carefully, as it may return undefined if the item is not loaded in the store.
        return item ? isMeetingMessage(item) : true; // If item is not found, disable reminders by default.
    }

    let isValidForReminder: boolean = true; // Default to true, disable reminders by default.

    // If the user is in expanded conversation view, we need to get the item from selectedNodeIds
    if (isItemPartOperation()) {
        // Expanded conversation view
        const item: ClientItem | null = getItemToShowFromNodeId(selectedNodeIds[0]);
        isValidForReminder = item ? isMeetingMessage(item) : true; // If item is not found, disable reminders by default.
    } else {
        // Otherwise, get the item from the rowKeys
        const currentRowId = MailRowDataPropertyGetter.getRowIdString(rowKeys[0], tableView);
        const currentItem: ClientItem | undefined = getMailStore().items.get(currentRowId);

        if (currentItem) {
            isValidForReminder = isMeetingMessage(currentItem);
        } else {
            //If it is not a valid rowKey, check for conversation view in MailListConversationView with single selection
            if (isMailListConversationView(tableView) && rowKeys.length === 1) {
                const conversation = getMailStore().conversations.get(
                    MailRowDataPropertyGetter.getConversationId(rowKeys[0], tableView)
                );
                if (conversation) {
                    const nodeId = conversation.conversationNodeIds?.[0] ?? '';
                    const item = getItemToShowFromNodeId(nodeId);
                    isValidForReminder = item ? isMeetingMessage(item) : true; // If item is not found, disable reminders by default.
                }
            }
        }
    }
    return isValidForReminder;
}
